import React from "react";
import { useTranslation } from "react-i18next";
import Callout from "components/callout/callout";
import useAccess from "@/hooks/useAccess";
import Section from "components/layout/section";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";
import TableList from "components/list/tableList/tableList";
import Tag from "components/tags/tag";
import Avatar from "components/user/Avatar";
import { AccessInvitation, AccessRole, FormStatus } from "@/types";
import IconButton from "../buttons/iconButton";
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import useLayout from "@/hooks/useLayout";

interface AccessListProps {
    storageId: string;
    displayHelper?: boolean;
}

const AccessList: React.FC<AccessListProps> = ({ storageId, displayHelper = true }) => {
    const { t } = useTranslation();

    const {
        filteredAccessInvitations,
        accessStatus,
        limitToRoles
    } = useAccess({ storageId })

    const {
        openDialog
    } = useLayout();

    const handleDeleteClick = (accessId: string) => {
        openDialog(`delete_access`, { storageId, accessId })
    }

    const canDelete = limitToRoles([AccessRole.OWNER]);

    const renderStatus = (accessInvitationData: AccessInvitation) => (
        <>
            {accessInvitationData.status === 'pending' && <Tag colorScheme="yellow">{t('titlePending')}</Tag>}
            {accessInvitationData.status === 'accepted' && <Tag colorScheme="green">{t('titleAccepted')}</Tag>}
            {accessInvitationData.status === 'declined' && <Tag colorScheme="red">{t('titleDeclined')}</Tag>}
            {accessInvitationData.isDeleted && <Tag colorScheme="gray">{t('titleDeleted')}</Tag>}
        </>
    );

    if (accessStatus === FormStatus.ERROR) {
        return <Callout type="error">{t('errorLoadingData')}</Callout>;
    }

    if (accessStatus !== 'idle' && filteredAccessInvitations.length === 0) {
        return (
            <Section>
                <Callout type="info">
                    {t('noAccessInvitations')}
                </Callout>
            </Section>
        );
    }

    return (
        <>
            {displayHelper && (
                <Callout type="info" id="accessListHelper">
                    {t('accessListHelper')}
                </Callout>
            )}

            <div className="flex flex-col gap-2 md:hidden">
                {accessStatus === FormStatus.LOADING ?
                    Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg bg-white p-3">
                            <p className="skeleton"></p>
                        </div>
                    ))
                : filteredAccessInvitations.map((accessInvitationData) => (
                    <div key={accessInvitationData.id} className="border border-gray-200 rounded-lg bg-white p-3 flex items-center gap-3">
                        <Avatar
                            avatarUrl={accessInvitationData.user?.other?.avatarUrl}
                            email={accessInvitationData.invitation.email}
                            hasAvatar={accessInvitationData.user?.other?.hasAvatar}
                            size="sm"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="truncate">{accessInvitationData.user?.name || accessInvitationData.invitation.email}</p>
                            <p className="text-sm capitalize text-gray-600">{t(accessInvitationData.role)}</p>
                        </div>
                        {renderStatus(accessInvitationData)}
                        {canDelete && (
                            <IconButton
                                icon={<DeleteIcon />}
                                size="small"
                                colorScheme="red"
                                onClick={() => handleDeleteClick(accessInvitationData.id || '')}
                            />
                        )}
                    </div>
                ))}
            </div>
    
            <div className="hidden md:block">
                <TableList>
                    <TableListRow isHeader>
                        <TableListCell>{t('titleEmail')}</TableListCell>
                        <TableListCell>{t('titleRole')}</TableListCell>
                        <TableListCell>{t('titleStatus')}</TableListCell>
                        <TableListCell></TableListCell>
                    </TableListRow>
                    { accessStatus === FormStatus.LOADING ?
                        Array.from({ length: 3 }).map((_, index) => (
                            <TableListRow key={index} hoverEffect={false}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><Tag isLoading={true} colorScheme={`gray`} /></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    : filteredAccessInvitations.map((accessInvitationData) => (
                        <TableListRow key={accessInvitationData.id} hoverEffect={false}>
                            <TableListCell>
                                <Avatar
                                    avatarUrl={accessInvitationData.user?.other?.avatarUrl}
                                    email={accessInvitationData.invitation.email}
                                    hasAvatar={accessInvitationData.user?.other?.hasAvatar}
                                    size="sm"
                                />
                                <span className="truncate">{accessInvitationData.user?.name || accessInvitationData.invitation.email}</span>
                            </TableListCell>
                            <TableListCell className="capitalize">{t(accessInvitationData.role)}</TableListCell>
                            <TableListCell>
                                {renderStatus(accessInvitationData)}
                            </TableListCell>
                            <TableListCell justifyContent="end">
                                {canDelete && (
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        size="small"
                                        colorScheme="red"
                                        onClick={() => handleDeleteClick(accessInvitationData.id || '')}
                                    />
                                )}
                            </TableListCell>
                        </TableListRow>
                    ))}
                </TableList>
            </div>
        </>
    );
};

export default AccessList;
